import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { connect } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { format } from "date-fns";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import PulseLoader from "react-spinners/PulseLoader";
import {
  auth,
  PropertyServiceStatusFilterValue,
  PropertyServiceStatusFilters,
  PropertyServiceStatusResponse,
  PropertyServiceStatusRow,
  PropertyServiceStatusSummary,
  Season,
} from "@mysnowscape/snow-lib";
import SearchBar from "../../../components/Filters/SearchBarWithFilters";
import HeaderBar from "../../../components/Header/HeaderBar";
import InfinitePageLoader from "../../../components/InfinitePageLoader";
import { formatAddress } from "../../../components/Cards/JobHistoryCard";
import CheckedCircleGreen from "../../../resources/checked-circle-green.svg";
import CrossCircleRed from "../../../resources/cross-circle-red.svg";
import useCityOptions from "../../../hooks/useCityOptions";
import useRouteOptions from "../../../hooks/useRouteOptions";
import propertyServiceStatus from "../../../services/axios/propertyServiceStatus";
import { formatErrorMessage } from "../../../services/axios/APIError";
import { CurrentUser, ReduxState } from "../../../store/storeTypes";

const SERVICE_STATUS_PAGE_SIZE = 30;
const DEFAULT_HOURS = 24;
const DEFAULT_SERVICE_STATUS: PropertyServiceStatusFilterValue = "both";
const SERVICE_STATUS_PATH = "/admin/property/service-status";

const EMPTY_SUMMARY: PropertyServiceStatusSummary = {
  total: 0,
  serviced: 0,
  notServiced: 0,
  inProgress: 0,
};

type SelectOption = {
  value: string;
  label: string;
};

type TimeframeOption = {
  value: number;
  label: string;
};

type UserCompaniesResponse = {
  userCompanies?: Array<{
    companyId: string;
    companyName: string;
    isPaid?: boolean;
  }>;
};

type SeasonSelectOption = {
  value: Season;
  label: string;
};

const seasonOptions: SeasonSelectOption[] = [
  { value: Season.snow, label: "Snow" },
  { value: Season.landscape, label: "Landscape" },
];

const PRESET_HOURS = [8, 24, 48, 72] as const;

const timeframeOptions: TimeframeOption[] = [
  { value: 8, label: "8h" },
  { value: 24, label: "24h" },
  { value: 48, label: "48h" },
  { value: 72, label: "72h" },
];

function isPresetHours(hours: number): hours is (typeof PRESET_HOURS)[number] {
  return PRESET_HOURS.includes(hours as (typeof PRESET_HOURS)[number]);
}

function parseHoursParam(raw: string | null): number {
  if (raw == null) return DEFAULT_HOURS;
  const parsed = Math.round(Number(raw));
  return isPresetHours(parsed) ? parsed : DEFAULT_HOURS;
}

const statusFilterOptions: Array<{
  value: PropertyServiceStatusFilterValue;
  label: string;
}> = [
  { value: "both", label: "Both" },
  { value: "serviced", label: "Serviced" },
  { value: "not_serviced", label: "Not serviced" },
];

const selectStyles = {
  control: (baseStyles: Record<string, unknown>) => ({
    ...baseStyles,
    borderColor: "rgb(204, 204, 204)",
    minHeight: 40,
  }),
};

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function buildQueryParamString(
  filters: PropertyServiceStatusFilters,
  searchTerm: string | null,
) {
  const queryParams = new URLSearchParams();
  queryParams.set(
    "companies",
    filters.companies.length > 0 ? filters.companies.join(",") : "all",
  );
  queryParams.set("season", filters.season);
  queryParams.set("hours", String(filters.hours));
  queryParams.set("serviceStatus", filters.serviceStatus);
  if (filters.routes.length > 0) {
    queryParams.set("routes", filters.routes.join(","));
  }
  if (filters.city) {
    queryParams.set("city", filters.city);
  }
  if (searchTerm) {
    queryParams.set("searchTerm", searchTerm);
  }
  return queryParams.toString();
}

function getFilterArray(
  queryParams: URLSearchParams,
  currentCompanyId?: string,
  defaultSeason: PropertyServiceStatusFilters["season"] = Season.snow,
): PropertyServiceStatusFilters {
  const companies = queryParams.has("companies")
    ? queryParams.get("companies")!.split(",").filter(Boolean)
    : currentCompanyId
      ? [currentCompanyId]
      : [];

  const seasonParam = queryParams.get("season");

  return {
    companies: companies.includes("all") ? [] : companies,
    season:
      seasonParam === Season.snow || seasonParam === Season.landscape
        ? seasonParam
        : defaultSeason,
    routes: queryParams.has("routes")
      ? queryParams.get("routes")!.split(",").filter(Boolean)
      : [],
    city: queryParams.get("city") || null,
    hours: parseHoursParam(
      queryParams.has("hours") ? queryParams.get("hours") : null,
    ),
    serviceStatus:
      queryParams.get("serviceStatus") === "serviced" ||
      queryParams.get("serviceStatus") === "not_serviced"
        ? (queryParams.get("serviceStatus") as PropertyServiceStatusFilterValue)
        : DEFAULT_SERVICE_STATUS,
  };
}

function ServiceStatusPage({ currentUser }: { currentUser: CurrentUser }) {
  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const queryFilters = getFilterArray(
    queryParams,
    currentUser.currentCompanyId,
    getDefaultSeason(currentUser.lastUsedSeason),
  );
  const [searchTerm, setSearchTerm] = useState<string | null>(
    queryParams.has("searchTerm") ? queryParams.get("searchTerm") : null,
  );
  const initialHours = parseHoursParam(
    queryParams.has("hours") ? queryParams.get("hours") : null,
  );
  const [filters, setFilters] = useState<PropertyServiceStatusFilters>({
    ...queryFilters,
    hours: initialHours,
  });
  const lastCompanyIdRef = useRef(currentUser.currentCompanyId);
  const canViewJobHistory =
    currentUser.currentCompanyId != null &&
    auth.can(currentUser, "view-job-history", currentUser.currentCompanyId);

  const companyQuery = useQuery<UserCompaniesResponse>({
    queryKey: ["user/" + currentUser.uid + "/companies"],
    enabled: currentUser.uid != null,
  });
  const companyOptions =
    companyQuery.data?.userCompanies
      ?.filter((company) =>
        auth.canAdminCompany(currentUser, company.companyId),
      )
      ?.map((company) => ({
        label: company.companyName + (company.isPaid ? "" : " (inactive)"),
        value: company.companyId,
      })) ?? [];
  const companyNameById = useMemo(
    () =>
      new Map(
        companyQuery.data?.userCompanies?.map((company) => [
          company.companyId,
          company.companyName,
        ]) ?? [],
      ),
    [companyQuery.data?.userCompanies],
  );

  const selectedSingleCompany =
    filters.companies.length === 1 ? filters.companies[0] : null;

  const { routeOptions, isPending: routeOptionsPending } = useRouteOptions(
    selectedSingleCompany,
    filters.season,
  );

  const { cityOptions, isPending: cityOptionsPending } = useCityOptions(
    selectedSingleCompany,
  );

  const query = useInfiniteQuery<PropertyServiceStatusResponse>({
    queryKey: ["property-service-status", filters, searchTerm],
    initialPageParam: 0,
    enabled: canViewJobHistory,
    retry: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.rows.length < SERVICE_STATUS_PAGE_SIZE
        ? null
        : Number(lastPageParam) + 1,
    queryFn: ({ pageParam }) =>
      propertyServiceStatus.getPropertyServiceStatus({
        searchPhrase: searchTerm,
        filters,
        take: SERVICE_STATUS_PAGE_SIZE,
        skip: Number(pageParam) * SERVICE_STATUS_PAGE_SIZE,
      }),
  });

  const rows = useMemo(
    () =>
      query.data?.pages.reduce<PropertyServiceStatusRow[]>(
        (allRows, page) => allRows.concat(page.rows),
        [],
      ) ?? [],
    [query.data?.pages],
  );

  const summary = query.data?.pages[0]?.summary ?? EMPTY_SUMMARY;
  const showCompanyColumn = filters.companies.length !== 1;

  const setFiltersAndUrl = useCallback(
    (newFilters: PropertyServiceStatusFilters, newSearchTerm = searchTerm) => {
      navigate(
        SERVICE_STATUS_PATH +
          "?" +
          buildQueryParamString(newFilters, newSearchTerm),
        {},
      );
      setFilters(newFilters);
    },
    [navigate, searchTerm],
  );

  useEffect(() => {
    const nextCompanyId = currentUser.currentCompanyId;
    if (lastCompanyIdRef.current === nextCompanyId) return;
    lastCompanyIdRef.current = nextCompanyId;
    if (!nextCompanyId) return;

    const newFilters = {
      ...filters,
      companies: [nextCompanyId],
      routes: [],
      city: null,
    };
    setFiltersAndUrl(newFilters);
  }, [currentUser.currentCompanyId, filters, setFiltersAndUrl]);

  useEffect(() => {
    const newFilters = getFilterArray(
      queryParams,
      currentUser.currentCompanyId,
      getDefaultSeason(currentUser.lastUsedSeason),
    );
    const newSearchTerm = queryParams.get("searchTerm") ?? null;
    setFilters(newFilters);
    setSearchTerm(newSearchTerm);
    // This is just to watch for url changes so we don't need to watch lastUsedSeason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, currentUser.currentCompanyId]);

  const updateFilters = (changes: Partial<PropertyServiceStatusFilters>) => {
    setFiltersAndUrl({
      ...filters,
      ...changes,
    });
  };

  const handleSearch = (value: string) => {
    const newSearchTerm = value.trim() || null;
    navigate(
      SERVICE_STATUS_PATH + "?" + buildQueryParamString(filters, newSearchTerm),
      {},
    );
    setSearchTerm(newSearchTerm);
  };

  if (!canViewJobHistory) {
    return (
      <div className="flex flex-col items-center">
        <HeaderBar title="Service Status" />
        <div
          className="w-full px-1 lg:px-10 py-8 text-center text-gray-600"
          data-testid="service-status-permission-denied"
        >
          You do not have permission to view service status.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <HeaderBar
        title="Service Status"
        buttons={[
          {
            color: "blue",
            label: "Refresh",
            onClick: async () => {
              await query.refetch();
            },
          },
        ]}
      />

      <div className="w-full px-1 lg:px-10">
        <SearchBar
          autoFocus={false}
          onSearch={handleSearch}
          placeholder="Search property or address"
          value={searchTerm ?? ""}
          widthClass="w-full max-w-md"
        >
          <FilterField label="Companies" testId="service-status-company-filter">
            <Select<SelectOption, true>
              isClearable
              isLoading={companyQuery.isPending}
              isMulti
              options={companyOptions}
              placeholder="Filter by Company"
              styles={selectStyles}
              value={companyOptions.filter((option) =>
                filters.companies.includes(option.value),
              )}
              onChange={(options) => {
                const companies = options.map((option) => option.value);
                setFiltersAndUrl({
                  ...filters,
                  companies,
                  routes: [],
                  city: null,
                });
              }}
            />
          </FilterField>

          <FilterField label="Season">
            <Select<SeasonSelectOption>
              options={seasonOptions}
              styles={selectStyles}
              value={seasonOptions.find(
                (option) => option.value === filters.season,
              )}
              onChange={(option) =>
                updateFilters({
                  season:
                    option?.value === Season.landscape
                      ? Season.landscape
                      : Season.snow,
                  routes: [],
                  city: null,
                })
              }
            />
          </FilterField>

          <FilterField
            disabled={selectedSingleCompany === null}
            label="Routes"
            testId="service-status-routes-filter"
          >
            <Select<SelectOption, true>
              isClearable
              isDisabled={selectedSingleCompany === null}
              isLoading={routeOptionsPending && selectedSingleCompany !== null}
              isMulti
              options={routeOptions}
              placeholder="All routes"
              styles={selectStyles}
              value={routeOptions.filter((option) =>
                filters.routes.includes(option.value),
              )}
              onChange={(options) =>
                updateFilters({
                  routes: options.map((option) => option.value),
                })
              }
            />
          </FilterField>

          <FilterField
            disabled={selectedSingleCompany === null}
            label="City"
            testId="service-status-city-filter"
          >
            <Select<SelectOption>
              isClearable
              isDisabled={selectedSingleCompany === null}
              isLoading={cityOptionsPending && selectedSingleCompany !== null}
              options={cityOptions}
              placeholder="Filter by City"
              styles={selectStyles}
              value={
                filters.city
                  ? cityOptions.find((option) => option.value === filters.city)
                  : null
              }
              onChange={(option) =>
                updateFilters({
                  city: option?.value ?? null,
                })
              }
            />
          </FilterField>

          <FilterField
            label="Timeframe"
            testId="service-status-timeframe-filter"
          >
            <Select<TimeframeOption>
              options={timeframeOptions}
              styles={selectStyles}
              value={timeframeOptions.find(
                (option) => option.value === filters.hours,
              )}
              onChange={(option) =>
                updateFilters({ hours: option?.value ?? DEFAULT_HOURS })
              }
            />
          </FilterField>

          <FilterField label="Service status">
            <div className="flex flex-wrap gap-2">
              {statusFilterOptions.map((option) => (
                <button
                  className={`border rounded-md px-3 py-2 text-sm font-semibold shadow-sm ${
                    filters.serviceStatus === option.value
                      ? "bg-snow-blue-3 border-snow-blue-3 text-full-white"
                      : "bg-full-white border-[#cccccc] text-full-black hover:bg-slate-50"
                  }`}
                  key={option.value}
                  onClick={() => updateFilters({ serviceStatus: option.value })}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </FilterField>
          {filters.companies.length !== 1 ? (
            <p className="text-gray-500 text-xs">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-4 mr-1 inline"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                />
              </svg>
              Some filters are disabled since you are filtering by more than one
              company.
            </p>
          ) : null}
        </SearchBar>

        {query.isPending ? (
          <PulseLoader
            color="#cccccc"
            size={15}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="text-center mt-20"
          />
        ) : query.isError ? (
          <div className="text-center py-8" data-testid="service-status-error">
            <p className="text-snow-red mb-4">
              {formatErrorMessage(query.error, "Unable to load service status")}
            </p>
            <button
              className="border rounded-md px-4 py-2 text-sm font-semibold shadow-sm bg-snow-blue-3 border-snow-blue-3 text-full-white hover:opacity-90"
              onClick={() => query.refetch()}
              type="button"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1 my-4 text-sm text-description-gray">
              <SummaryStat label="Matching Properties" value={summary.total} />
              {filters.serviceStatus === "both" && (
                <>
                  <SummaryStat
                    className="text-snow-green"
                    label="Serviced"
                    value={summary.serviced}
                  />
                  <SummaryStat
                    className="text-snow-red"
                    label="Not serviced"
                    value={summary.notServiced}
                  />
                  <SummaryStat
                    className="text-orange"
                    label="In progress"
                    value={summary.inProgress}
                  />
                </>
              )}
            </div>

            <InfinitePageLoader
              loadNextPage={query.fetchNextPage}
              hasMorePages={query.hasNextPage}
              loading={query.isFetchingNextPage}
            >
              <table className="table">
                <thead>
                  <tr>
                    <th>Property</th>
                    {showCompanyColumn ? <th>Company</th> : null}
                    <th className="hidden lg:table-cell">Routes</th>
                    <th>Status</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((row) => (
                      <ServiceStatusTableRow
                        companyName={
                          companyNameById.get(row.companyId) ?? row.companyId
                        }
                        key={row.companyId + row.jobId}
                        row={row}
                        showCompanyColumn={showCompanyColumn}
                      />
                    ))
                  ) : (
                    <tr>
                      <td
                        className="text-center text-gray-500 py-8"
                        colSpan={showCompanyColumn ? 5 : 4}
                      >
                        No properties found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </InfinitePageLoader>
          </>
        )}
      </div>
    </div>
  );
}

function FilterField({
  children,
  disabled = false,
  label,
  testId,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  label: string;
  testId?: string;
}) {
  return (
    <div data-disabled={disabled ? "1" : "0"} data-testid={testId}>
      <label className="block text-xs font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

function SummaryStat({
  className = "text-snow-primary",
  label,
  value,
}: {
  className?: string;
  label: string;
  value: number;
}) {
  return (
    <div>
      <span className="font-medium">{label}: </span>
      <span
        className={`font-bold ${className}`}
        data-testid={`summary-${label}`}
      >
        {value}
      </span>
    </div>
  );
}

function ServiceStatusTableRow({
  companyName,
  row,
  showCompanyColumn,
}: {
  companyName: string;
  row: PropertyServiceStatusRow;
  showCompanyColumn: boolean;
}) {
  const routeNames = getRouteNames(row);

  return (
    <tr data-testid={`service-status-row-${row.jobId}`}>
      <td>
        <Link
          className="font-bold hover:underline"
          to={`/admin/property/${row.jobId}`}
        >
          {row.name}
        </Link>
        <div className="text-xs text-description-gray mt-0.5">
          {formatAddress(row)}
        </div>
        <div className="text-xs text-description-gray lg:hidden">
          {routeNames}
        </div>
      </td>
      {showCompanyColumn ? <td>{companyName}</td> : null}
      <td className="hidden lg:table-cell" data-testid={`routes-${row.jobId}`}>
        {routeNames}
      </td>
      <td>
        <ServiceStatusLabel row={row} />
      </td>
      <td>
        <Link className="underline" to={`/admin/property/${row.jobId}`}>
          View
        </Link>
      </td>
    </tr>
  );
}

function ServiceStatusLabel({ row }: { row: PropertyServiceStatusRow }) {
  const lastServicedAtDisplay = formatServiceDate(row.lastServicedAt);

  if (row.serviceStatus === "serviced") {
    const content = (
      <span
        className="inline-flex items-center gap-2 text-snow-green font-semibold"
        data-testid={`status-serviced-${row.jobId}`}
      >
        <img alt="" className="w-4 h-4" src={CheckedCircleGreen} />
        <span>
          <span>Serviced</span>
          {lastServicedAtDisplay ? (
            <span className="block text-xs">{lastServicedAtDisplay}</span>
          ) : null}
        </span>
      </span>
    );
    return row.latestJobHistoryId ? (
      <Link
        className="hover:underline"
        to={`/admin/property/history/${row.latestJobHistoryId}`}
      >
        {content}
      </Link>
    ) : (
      content
    );
  }

  if (row.serviceStatus === "in_progress") {
    const content = (
      <span
        className="inline-flex items-center gap-2 text-orange font-semibold"
        data-testid={`status-in-progress-${row.jobId}`}
      >
        <span className="w-4 h-4 rounded-full border-2 border-orange inline-block" />
        In progress
      </span>
    );
    return row.latestJobHistoryId ? (
      <Link
        className="hover:underline"
        to={`/admin/property/history/${row.latestJobHistoryId}`}
      >
        {content}
      </Link>
    ) : (
      content
    );
  }

  const lastDoneLabel = formatLastDoneLabel(row.lastServicedAt);
  const content = (
    <span
      className="inline-flex items-center gap-2 text-snow-red font-semibold"
      data-testid={`status-not-serviced-${row.jobId}`}
    >
      <img alt="" className="w-4 h-4" src={CrossCircleRed} />
      <span>
        <span>Not serviced</span>
        {lastDoneLabel ? (
          <span className="block text-xs">{lastDoneLabel}</span>
        ) : null}
      </span>
    </span>
  );
  return row.latestJobHistoryId ? (
    <Link
      className="hover:underline"
      to={`/admin/property/history/${row.latestJobHistoryId}`}
    >
      {content}
    </Link>
  ) : (
    content
  );
}

function getRouteNames(row: PropertyServiceStatusRow) {
  return row.routes.map((route) => route.routeName).join(", ");
}

function formatServiceDate(dateString: string | null) {
  if (!dateString) return "";
  return format(new Date(dateString), "MMM d h:mm bbb");
}

function formatLastDoneLabel(dateString: string | null) {
  if (!dateString) return "";
  return `Last done ${format(new Date(dateString), "MMM d")}`;
}

function getDefaultSeason(lastUsedSeason?: string): Season {
  return lastUsedSeason === Season.landscape ? Season.landscape : Season.snow;
}

const mapStateToProps = (state: ReduxState) => {
  const { currentUser } = state;
  return { currentUser };
};

export { ServiceStatusPage };
export default connect(mapStateToProps)(ServiceStatusPage);
